import { Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import LessonCard from './components/LessonCard';
import { allAPCalculusABLessons } from './data';
import SEOHead from '../../../components/SEOHead';

export default function APCalculusABPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<string>('all');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Course',
      name: 'AP Calculus AB | Limits, Derivatives & Integrals | Augustine Anyimadu-Ahenkae',
      description: 'Complete AP Calculus AB curriculum using the Rule of Four approach. Covers limits, continuity, differentiation, integration, differential equations, and applications. Aligned with College Board AP framework.',
      url: `${siteUrl}/grades/ap-calculus-ab`,
      provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
      educationalLevel: 'Advanced Placement',
      inLanguage: 'en',
      hasCourseInstance: [
        { '@type': 'CourseInstance', name: 'Unit 1: Limits and Continuity', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 2: Differentiation — Definition & Properties', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 3: Composite, Implicit & Inverse Functions', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 4: Contextual Applications of Differentiation', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 5: Analytical Applications of Differentiation', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 6: Integration and Accumulation', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 7: Differential Equations', courseMode: 'online' },
        { '@type': 'CourseInstance', name: 'Unit 8: Applications of Integration', courseMode: 'online' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is the Rule of Four in AP Calculus?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The Rule of Four is the NCTM-recommended approach to teaching calculus using four representations: Algebraic (equations), Graphical (visual), Numerical (tables), and Verbal (written reasoning). Every lesson uses all four representations to build deep understanding.',
          },
        },
        {
          '@type': 'Question',
          name: 'What units are covered in AP Calculus AB?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'AP Calculus AB covers 8 units: Limits and Continuity, Differentiation (Definition & Properties), Composite/Implicit/Inverse Differentiation, Contextual Applications of Differentiation, Analytical Applications of Differentiation, Integration and Accumulation, Differential Equations, and Applications of Integration.',
          },
        },
      ],
    },
  ];

  // Get unique units
  const units = useMemo(() => {
    const unitSet = new Set(allAPCalculusABLessons.map(lesson => lesson.unit));
    return ['all', ...Array.from(unitSet)];
  }, []);

  // Filter lessons
  const filteredLessons = useMemo(() => {
    return allAPCalculusABLessons.filter(lesson => {
      const matchesSearch = lesson.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           lesson.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           lesson.unit.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesUnit = selectedUnit === 'all' || lesson.unit === selectedUnit;
      return matchesSearch && matchesUnit;
    });
  }, [searchTerm, selectedUnit]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <SEOHead
        title="AP Calculus AB Curriculum | Limits Derivatives Integrals | Augustine Anyimadu-Ahenkae"
        description="Master AP Calculus AB with comprehensive lessons on limits, derivatives, integrals, and differential equations. Rule of Four methodology, 240+ practice questions, College Board aligned. Prepare for a 5 on the AP exam."
        canonical="/grades/ap-calculus-ab"
        keywords="AP Calculus AB, limits derivatives integrals, AP Calculus lessons, Rule of Four calculus, AP exam prep calculus, Fundamental Theorem Calculus"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 text-white">
        <div className="container mx-auto px-4 py-16">
          <Link 
            to="/grades/hub" 
            className="inline-flex items-center gap-2 text-blue-100 hover:text-white mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line text-xl"></i>
            <span className="text-lg">Back to Grades Hub</span>
          </Link>
          
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <i className="ri-function-line text-4xl"></i>
            </div>
            <div>
              <h1 className="text-5xl font-bold mb-2">AP Calculus AB</h1>
              <p className="text-blue-100 text-xl">Master limits, derivatives, and integrals • College Board AP Exam Prep</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">{allAPCalculusABLessons.length}</div>
              <div className="text-blue-100">Comprehensive Lessons</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">{allAPCalculusABLessons.length * 30}</div>
              <div className="text-blue-100">Practice Questions</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">8</div>
              <div className="text-blue-100">Major Units</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <div className="text-3xl font-bold">5</div>
              <div className="text-blue-100">AP Exam Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Course Overview */}
      <div className="container mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Course Overview</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            AP Calculus AB is equivalent to a first semester college calculus course. The curriculum covers limits, derivatives, 
            integrals, and the Fundamental Theorem of Calculus. Every lesson follows the <strong>Rule of Four</strong> approach: 
            Algebraic, Graphical, Numerical, and Verbal representations.
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div className="border-l-4 border-blue-600 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Units Covered</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span><strong>Unit 1:</strong> Limits and Continuity</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span><strong>Unit 2:</strong> Differentiation - Definition & Properties</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span><strong>Unit 3:</strong> Composite, Implicit, & Inverse Functions</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line text-green-600 text-xl mt-0.5"></i>
                  <span><strong>Unit 4:</strong> Contextual Applications of Differentiation</span>
                </li>
              </ul>
            </div>

            <div className="border-l-4 border-purple-600 pl-4">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Standards Alignment</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-graduation-cap-line text-purple-600 text-xl mt-0.5"></i>
                  <span>AP College Board Curriculum Framework</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-book-2-line text-purple-600 text-xl mt-0.5"></i>
                  <span>Common Core Mathematics Standards</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-draft-line text-purple-600 text-xl mt-0.5"></i>
                  <span>NCTM Process Standards</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-focus-3-line text-purple-600 text-xl mt-0.5"></i>
                  <span>Rule of Four Methodology</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <i className="ri-lightbulb-flash-line text-yellow-600"></i>
              The Rule of Four in Every Lesson
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="ri-functions text-2xl"></i>
                </div>
                <div className="font-semibold text-gray-900">Algebraic</div>
                <div className="text-sm text-gray-600">Equations & Symbols</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-600 text-white rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="ri-line-chart-line text-2xl"></i>
                </div>
                <div className="font-semibold text-gray-900">Graphical</div>
                <div className="text-sm text-gray-600">Visual Interpretation</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="ri-table-line text-2xl"></i>
                </div>
                <div className="font-semibold text-gray-900">Numerical</div>
                <div className="text-sm text-gray-600">Tables & Data</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-purple-600 text-white rounded-lg flex items-center justify-center mx-auto mb-2">
                  <i className="ri-article-line text-2xl"></i>
                </div>
                <div className="font-semibold text-gray-900">Verbal</div>
                <div className="text-sm text-gray-600">Written Explanations</div>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="relative">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
              <input
                type="text"
                placeholder="Search lessons by topic or keyword..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={selectedUnit}
              onChange={(e) => setSelectedUnit(e.target.value)}
              className="px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
            >
              {units.map((unit) => (
                <option key={unit} value={unit}>
                  {unit === 'all' ? 'All Units' : unit}
                </option>
              ))}
            </select>
          </div>

          {filteredLessons.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <i className="ri-search-line text-4xl mb-2"></i>
              <p>No lessons found matching your search.</p>
            </div>
          )}
        </div>

        {/* Lessons Grid */}
        <div className="grid gap-6">
          {filteredLessons.map((lesson) => (
            <LessonCard key={lesson.id} lesson={lesson} />
          ))}
        </div>

        {/* AP Exam Resources */}
        <div className="mt-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl shadow-lg p-8 text-white">
          <h2 className="text-3xl font-bold mb-4">AP Exam Preparation</h2>
          <p className="text-blue-100 text-lg mb-6">
            Ready for the AP Calculus AB exam? Access full-length practice tests, free-response questions, and scoring guides.
          </p>
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/grades/ap-calculus-ab-practice-exam"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-4 transition-all hover:scale-105"
            >
              <i className="ri-file-list-3-line text-3xl mb-2"></i>
              <div className="font-semibold">Practice Exam 1</div>
              <div className="text-sm text-blue-100">Full-length MCQ & FRQ</div>
            </Link>
            <Link
              to="/grades/ap-calculus-scoring-guide"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-4 transition-all hover:scale-105"
            >
              <i className="ri-bar-chart-box-line text-3xl mb-2"></i>
              <div className="font-semibold">Scoring Guide</div>
              <div className="text-sm text-blue-100">Understand your score</div>
            </Link>
            <Link
              to="/grades/ap-calculus-bc"
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg p-4 transition-all hover:scale-105"
            >
              <i className="ri-rocket-line text-3xl mb-2"></i>
              <div className="font-semibold">AP Calculus BC</div>
              <div className="text-sm text-blue-100">Advanced topics & series</div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}